import { StudentClassificationStatus } from '../types/studentClassification';
import type { LeadPersonaType, LeadTemperatureType } from '../types/lead';
import type { MultiSelectOption } from '../components/common/MultiSelectFilter/MultiSelectFilter';
import { LEAD_CANCELLATION_REASON_OPTIONS } from './leadCancellationReason';
import {
  LEAD_QUALIFICATION_INVESTMENT_INTEREST_OPTIONS,
  LEAD_QUALIFICATION_MAIN_GOAL_OPTIONS,
  LEAD_QUALIFICATION_START_TIME_OPTIONS,
} from '../pages/Checkout/checkout.constants';
import { colors } from '../theme/colors';

/** Dot/badge colors aligned with Badge variants used for lifecycle tags. */
export const COMMERCIAL_CLASSIFICATION_FILTER_OPTIONS: MultiSelectOption[] = [
  { value: StudentClassificationStatus.LEAD, label: 'Lead', color: colors.classificationLeadText },
  { value: StudentClassificationStatus.PRE_STUDENT, label: 'Pré-aluna', color: '#F59E0B' },
  { value: StudentClassificationStatus.PRE_STUDENT_INACTIVE, label: 'Pré-aluna inativa', color: '#F59E0B' },
  { value: StudentClassificationStatus.NEW_STUDENT, label: 'Aluna Nova', color: '#3B82F6' },
];

/** Journey tags for Orchestrator / Performance (new vs old student). */
export const STUDENT_JOURNEY_FILTER_OPTIONS: MultiSelectOption[] = [
  { value: StudentClassificationStatus.NEW_STUDENT, label: 'Aluna Nova', color: '#3B82F6' },
  { value: StudentClassificationStatus.OLD_STUDENT, label: 'Aluna Antiga', color: colors.personaNon },
];

/** Tag options for the onboarding follow-up screen (pré-aluna + aluna nova only). */
export const ONBOARDING_CLASSIFICATION_FILTER_OPTIONS: MultiSelectOption[] = [
  { value: StudentClassificationStatus.PRE_STUDENT, label: 'Pré-aluna', color: '#F59E0B' },
  { value: StudentClassificationStatus.PRE_STUDENT_INACTIVE, label: 'Pré-aluna inativa', color: '#F59E0B' },
  { value: StudentClassificationStatus.NEW_STUDENT, label: 'Aluna Nova', color: '#3B82F6' },
];

/**
 * Commercial temperature options (lead_classification_type).
 * Colors match lead_classification_rules.bg_color values.
 */
export const COMMERCIAL_LEAD_TEMPERATURE_FILTER_OPTIONS: Array<
  MultiSelectOption & { value: LeadTemperatureType }
> = [
  { value: 'super_hot', label: 'Super quente', color: '#B91C1C' },
  { value: 'hot', label: 'Quente', color: '#EF4444' },
  { value: 'warm', label: 'Morno', color: colors.leadTemperatureWarm },
  { value: 'cold', label: 'Frio', color: '#A4CBE6' },
];

export const COMMERCIAL_PERSONA_FILTER_OPTIONS: Array<
  MultiSelectOption & { value: LeadPersonaType }
> = [
  { value: 'super_persona', label: 'Super Persona', color: colors.personaSuper },
  { value: 'persona', label: 'Persona', color: colors.persona },
  { value: 'non_persona', label: 'Não Persona', color: colors.personaNon },
];

export const COMMERCIAL_BOOLEAN_FILTER_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
];

/** Multi-select options for the Pagamento → Pago group (Sim/Não). */
export const COMMERCIAL_PAID_STATUS_FILTER_OPTIONS: MultiSelectOption[] = [
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
];

/** Multi-select options for the Pagamento → Forma de pagamento group. */
export const COMMERCIAL_PAYMENT_METHOD_FILTER_OPTIONS: MultiSelectOption[] = [
  { value: 'pix', label: 'Pix' },
  { value: 'credit_card', label: 'Cartão de crédito' },
];

export const COMMERCIAL_INVESTMENT_INTEREST_FILTER_OPTIONS = LEAD_QUALIFICATION_INVESTMENT_INTEREST_OPTIONS;
export const COMMERCIAL_START_TIME_FILTER_OPTIONS = LEAD_QUALIFICATION_START_TIME_OPTIONS;
export const COMMERCIAL_MAIN_GOAL_FILTER_OPTIONS = LEAD_QUALIFICATION_MAIN_GOAL_OPTIONS;
export const COMMERCIAL_CANCELLATION_REASON_FILTER_OPTIONS = LEAD_CANCELLATION_REASON_OPTIONS;
export { COMMERCIAL_STAGE_FILTER_OPTIONS } from './commercialStage';

export const COMMERCIAL_OWNER_UNSET_FILTER_VALUE = '__unset__';

export type CommercialBooleanFilterValue = '' | 'yes' | 'no';
export type CommercialPaidStatusFilterValue = 'yes' | 'no';
export type CommercialPaymentMethodFilterValue = 'pix' | 'credit_card';

export const parseCommercialBooleanFilter = (
  value: CommercialBooleanFilterValue,
): boolean | undefined => {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return undefined;
};

/** Converts multi-select Pago (Sim/Não) into a single boolean filter for the API. */
export const parseCommercialPaidStatusesFilter = (
  values: string[],
): boolean | undefined => {
  const hasYes = values.includes('yes');
  const hasNo = values.includes('no');
  if (hasYes && !hasNo) return true;
  if (hasNo && !hasYes) return false;
  return undefined;
};
